import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tokenManager } from "services/TokenManager";
import { KeycloakService } from '../../auth/KeycloakService';
import * as Sentry from "@sentry/react";

const AuthStateContext = createContext(null);
const AuthDispatchContext = createContext(null);

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'auth-context' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'auth-context' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'auth-context',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

// Auth hint helper for optimistic authentication
const getAuthHint = () => {
  try {
    return localStorage.getItem('auth_hint') === 'true';
  } catch (e) {
logInfo('Failed to read auth hint (storage unavailable)');    return false;
  }
};

const PROVIDERS = {
  EMAIL: 'email',
  GOOGLE: 'google'
};

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  user: null,
  error: null,
  provider: null,
  sessionInfo: null,
  lastAction: null
};

const AUTH_ACTIONS = {
  INITIALIZATION: {
    START: 'INIT_START',
    SUCCESS: 'INIT_SUCCESS',
    FAILURE: 'INIT_FAILURE',
    NO_SESSION: 'INIT_NO_SESSION'
  },
  EMAIL: {
    SIGNUP_START: 'EMAIL_SIGNUP_START',
    SIGNUP_SUCCESS: 'EMAIL_SIGNUP_SUCCESS',
    SIGNUP_FAILURE: 'EMAIL_SIGNUP_FAILURE',
    LOGIN_START: 'EMAIL_LOGIN_START',
    LOGIN_SUCCESS: 'EMAIL_LOGIN_SUCCESS',
    LOGIN_FAILURE: 'EMAIL_LOGIN_FAILURE'
  },
  OTP: {
    SEND_START: 'OTP_SEND_START',
    SEND_SUCCESS: 'OTP_SEND_SUCCESS',
    SEND_FAILURE: 'OTP_SEND_FAILURE',
    VERIFY_START: 'OTP_VERIFY_START',
    VERIFY_SUCCESS: 'OTP_VERIFY_SUCCESS',
    VERIFY_FAILURE: 'OTP_VERIFY_FAILURE',
    RESEND_START: 'OTP_RESEND_START',
    RESEND_SUCCESS: 'OTP_RESEND_SUCCESS',
    RESEND_FAILURE: 'OTP_RESEND_FAILURE'
  },
  PASSWORD: {
    FORGOT_START: 'PASSWORD_FORGOT_START',
    FORGOT_SUCCESS: 'PASSWORD_FORGOT_SUCCESS',
    FORGOT_FAILURE: 'PASSWORD_FORGOT_FAILURE',
    RESET_START: 'PASSWORD_RESET_START',
    RESET_SUCCESS: 'PASSWORD_RESET_SUCCESS',
    RESET_FAILURE: 'PASSWORD_RESET_FAILURE'
  },
  OAUTH: {
    START: 'OAUTH_START',
    SUCCESS: 'OAUTH_SUCCESS',
    FAILURE: 'OAUTH_FAILURE',
    CALLBACK_START: 'OAUTH_CALLBACK_START',
    CALLBACK_SUCCESS: 'OAUTH_CALLBACK_SUCCESS',
    CALLBACK_FAILURE: 'OAUTH_CALLBACK_FAILURE'
  },
  PROVIDER: {
    SWITCH_REQUIRED: 'PROVIDER_SWITCH_REQUIRED',
    SWITCH_START: 'PROVIDER_SWITCH_START',
    SWITCH_SUCCESS: 'PROVIDER_SWITCH_SUCCESS',
    SWITCH_FAILURE: 'PROVIDER_SWITCH_FAILURE'
  },
  SESSION: {
    REFRESH_START: 'SESSION_REFRESH_START',
    REFRESH_SUCCESS: 'SESSION_REFRESH_SUCCESS',
    REFRESH_FAILURE: 'SESSION_REFRESH_FAILURE',
    EXPIRED: 'SESSION_EXPIRED',
    UNAUTHORIZED: 'SESSION_UNAUTHORIZED'
  },
  ERROR: {
    SET: 'SET_ERROR',
    CLEAR: 'CLEAR_ERROR'
  },
  RESET: 'RESET_STATE'
};

// Core authentication error messages - synchronized with backend
const GENERIC_ERROR_MESSAGES = {
  // Network & Server Errors
  'NETWORK_ERROR': 'Network error. Please check your connection and try again.',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again later.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'SERVICE_ERROR': 'Service error. Please try again.',
  'API_CONNECTION_ERROR': 'Could not connect to server. Please try again.',
  'CONNECTION_ERROR': 'Connection error. Please try again.',
  'REQUEST_TIMEOUT': 'Request timed out. Please try again.',
  
  // Authentication & Session Errors
  'AUTHENTICATION_FAILED': 'Authentication failed. Please try again.',
  'AUTHENTICATION_INTERRUPTED': 'Authentication was interrupted.',
  'AUTHENTICATION_SERVICE_ERROR': 'Authentication service error. Please try again.',
  'INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
  'SESSION_EXPIRED': 'Your session has expired. Please sign in again.',
  'SESSION_ERROR': 'Session error. Please sign in again.',
  'SESSION_CHECK_ERROR': 'Session check failed. Please try again.',
  'AUTO_LOGIN_FAILED': 'Account created successfully, but auto-login failed. Please sign in manually.',
  'TOKEN_BLACKLISTED': 'Your session has been invalidated. Please sign in again.',
  'MISSING_TOKEN': 'Authentication token missing. Please sign in again.',
  
  // User Account Errors
  'USER_NOT_FOUND': 'User account not found.',
  'EMAIL_NOT_FOUND': 'Email not found in our system.',
  'EMAIL_ALREADY_REGISTERED': 'This email is already registered. Please sign in or use a different email address.',
  'ACCOUNT_LOCKED': 'Account temporarily locked due to multiple failed attempts.',
  'USER_CREATE_ERROR': 'Failed to create user account.',
  'ACCOUNT_CREATION_FAILED': 'Account creation failed. Please try again.',
  'ACCOUNT_SETUP_FAILED': 'Account setup failed. Please try again.',
  'ACCOUNT_UPDATE_FAILED': 'Failed to update account.',
  
  // Registration Errors
  'REGISTRATION_FAILED': 'Registration failed. Please try again.',
  'REGISTRATION_ERROR': 'Registration error. Please try again.',
  'VERIFICATION_FAILED': 'Verification failed. Please try again.',
  
  // Login Errors
  'LOGIN_FAILED': 'Login failed. Please try again.',
  
  // OTP Errors
  'SEND_OTP_FAILED': 'Failed to send verification code. Please try again.',
  'VERIFY_OTP_FAILED': 'Invalid verification code. Please try again.',
  'INVALID_OTP': 'Invalid verification code. Please try again.',
  'OTP_GENERATION_FAILED': 'Failed to generate verification code.',
  'EMAIL_SEND_FAILED': 'Failed to send email. Please try again later.',
  
  // Password Errors
  'FORGOT_PASSWORD_FAILED': 'Password reset request failed. Please try again.',
  'RESET_PASSWORD_FAILED': 'Password reset failed. Please try again.',
  'PASSWORD_RESET_FAILED': 'Password reset failed. Please try again.',
  'PASSWORD_REQUIREMENTS_NOT_MET': 'Password does not meet requirements.',
  
  // OAuth/Social Login Errors
  'OAUTH_INITIALIZATION_ERROR': 'Social login initialization failed',
  'OAUTH_CALLBACK_FAILED': 'Social login failed',
  'OAUTH_CONFIG_ERROR': 'OAuth configuration error.',
  'GOOGLE_CONNECTION_ERROR': 'Could not connect to Google. Please try again.',
  'GOOGLE_SIGNIN_FAILED': 'Google sign-in failed. Please try again.',
  'GOOGLE_INFO_ERROR': 'Failed to retrieve Google account information.',
  'GOOGLE_INFO_RETRIEVAL_FAILED': 'Failed to retrieve Google account information.',
  'MISSING_EMAIL': 'Email permission required for authentication.',
    'WRONG_PROVIDER': 'Please sign in using the method you originally registered with.',
  'USE_DIFFERENT_PROVIDER': 'Please use a different sign-in method.',
  // Validation Errors
  'VALIDATION_ERROR': 'Please check your input and try again.',
  'INVALID_REQUEST': 'Invalid request. Please try again.',
  'INVALID_JSON': 'Invalid data format. Please try again.',
  'INVALID_EMAIL': 'Invalid email format.',
  'INVALID_DATA': 'Invalid data. Please try again.',
  'INVALID_RESPONSE': 'Invalid response received. Please try again.',
  'INVALID_USER_DATA': 'Invalid user data. Please try again.',
  'INVALID_GOOGLE_DATA': 'Invalid Google account data.',
  'DATA_ERROR': 'Data processing error. Please try again.',
  
  // Rate Limiting
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'RATE_LIMITED': 'Too many attempts. Please wait before trying again.',
  
  // Database Errors
  'DB_ERROR': 'Database error. Please try again later.',
  'DATABASE_ERROR': 'Database error. Please try again later.',
  
  // Configuration & Service Errors
  'CONFIG_ERROR': 'Service configuration error. Please contact support.',
  'METHOD_NOT_ALLOWED': 'Invalid request method.',
  
  // Logout Errors
  'LOGOUT_ERROR': 'Logout failed. Please try again.',
  
  // User Search & Profile Errors
  'USER_SEARCH_ERROR': 'Failed to search for user.'
};

// Get safe error message
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return GENERIC_ERROR_MESSAGES['SERVER_ERROR'];
  }
  
  // ✅ CHECK INTERCEPTOR FIELDS FIRST!
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  // Check backend error code first (in response data)
  if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
    return GENERIC_ERROR_MESSAGES[error.response.data.code];
  }
  
  // Check direct error code
  if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
    return GENERIC_ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes
  if (error.response?.status === 401 || error.status === 401) {
    return GENERIC_ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  
  return GENERIC_ERROR_MESSAGES['SERVER_ERROR'];
};

// Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Validate redirect paths against whitelist
const ALLOWED_REDIRECT_PATHS = [
  '/dashboard',
  '/account',
  '/billing',
  '/deploy',
  '/volumes',
  '/ssh-keys',
  '/firewall',
  '/snapshots',
  '/images',
  '/event-log',
  '/pricing'
];

const isValidRedirectPath = (path) => {
  return ALLOWED_REDIRECT_PATHS.includes(path) || path === '/';
};

const authReducer = (state, action) => {
  switch (action.type) {
    // Initialization Actions
    case AUTH_ACTIONS.INITIALIZATION.START:
      return {
        ...state,
        isLoading: true,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.INITIALIZATION.SUCCESS:
      tokenManager.startProactiveRefresh();
      return {
        ...state,
        isInitialized: true,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        provider: action.payload.user?.provider,
        sessionInfo: action.payload.sessionInfo,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.SESSION.UNAUTHORIZED:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        sessionInfo: null,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.INITIALIZATION.FAILURE:
      return {
        ...state,
        isInitialized: true,
        isLoading: false,
        error: action.payload,
        lastAction: action.type
      };

    case AUTH_ACTIONS.INITIALIZATION.NO_SESSION:
      return {
        ...state,
        isInitialized: true,
        isLoading: false,
        lastAction: action.type
      };

    // Email Authentication Actions
    case AUTH_ACTIONS.EMAIL.SIGNUP_START:
    case AUTH_ACTIONS.EMAIL.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.EMAIL.SIGNUP_SUCCESS:
    case AUTH_ACTIONS.EMAIL.LOGIN_SUCCESS:
      tokenManager.startProactiveRefresh();
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        provider: action.payload.user?.provider || PROVIDERS.EMAIL,
        sessionInfo: action.payload.sessionInfo,
        error: null,
        lastAction: action.type
      };

    // OTP Actions
    case AUTH_ACTIONS.OTP.SEND_START:
    case AUTH_ACTIONS.OTP.VERIFY_START:
    case AUTH_ACTIONS.OTP.RESEND_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.OTP.SEND_SUCCESS:
    case AUTH_ACTIONS.OTP.RESEND_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.OTP.VERIFY_SUCCESS:
      tokenManager.startProactiveRefresh();
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        provider: action.payload.user?.provider,
        sessionInfo: action.payload.sessionInfo,
        error: null,
        lastAction: action.type
      };

    // Password Actions
    case AUTH_ACTIONS.PASSWORD.FORGOT_START:
    case AUTH_ACTIONS.PASSWORD.RESET_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.PASSWORD.FORGOT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.PASSWORD.RESET_SUCCESS:
      if (action.payload.autoLogin) {
        tokenManager.startProactiveRefresh();
      }
      return {
        ...state,
        isAuthenticated: action.payload.autoLogin || false,
        isLoading: false,
        user: action.payload.autoLogin ? action.payload.user : state.user,
        sessionInfo: action.payload.autoLogin ? action.payload.sessionInfo : state.sessionInfo,
        error: null,
        lastAction: action.type
      };

    // OAuth Actions
    case AUTH_ACTIONS.OAUTH.START:
    case AUTH_ACTIONS.OAUTH.CALLBACK_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        lastAction: action.type
      };

    case AUTH_ACTIONS.OAUTH.SUCCESS:
    case AUTH_ACTIONS.OAUTH.CALLBACK_SUCCESS:
      tokenManager.startProactiveRefresh();
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        provider: action.payload.provider,
        sessionInfo: action.payload.sessionInfo,
        error: null,
        lastAction: action.type
      };

    // Provider Switch Actions
    case AUTH_ACTIONS.PROVIDER.SWITCH_REQUIRED:
      return {
        ...state,
        isLoading: false,
        error: {
          code: 'WRONG_PROVIDER',
          message: action.payload.message || getSafeErrorMessage({ code: 'AUTHENTICATION_FAILED' }),
          provider: action.payload.provider,
          suggestedProvider: action.payload.suggestedProvider
        },
        lastAction: action.type
      };

    // Session Actions
    case AUTH_ACTIONS.SESSION.REFRESH_SUCCESS:
      return {
        ...state,
        sessionInfo: action.payload.sessionInfo,
        lastAction: action.type
      };

    case AUTH_ACTIONS.SESSION.EXPIRED:
      return {
        ...initialState,
        isInitialized: true,
        error: {
          code: 'SESSION_EXPIRED',
          message: getSafeErrorMessage({ code: 'SESSION_EXPIRED' })
        },
        lastAction: action.type
      };

    // Error Actions
    case AUTH_ACTIONS.ERROR.SET:
      return {
        ...state,
        error: {
          ...action.payload,
          message: getSafeErrorMessage(action.payload)
        },
        isLoading: false,
        lastAction: action.type
      };

    case AUTH_ACTIONS.ERROR.CLEAR:
      return {
        ...state,
        error: null,
        lastAction: action.type
      };

    // Failure Actions
    case AUTH_ACTIONS.EMAIL.SIGNUP_FAILURE:
    case AUTH_ACTIONS.EMAIL.LOGIN_FAILURE:
    case AUTH_ACTIONS.OTP.SEND_FAILURE:
    case AUTH_ACTIONS.OTP.VERIFY_FAILURE:
    case AUTH_ACTIONS.OTP.RESEND_FAILURE:
    case AUTH_ACTIONS.PASSWORD.FORGOT_FAILURE:
    case AUTH_ACTIONS.PASSWORD.RESET_FAILURE:
    case AUTH_ACTIONS.OAUTH.FAILURE:
    case AUTH_ACTIONS.OAUTH.CALLBACK_FAILURE:
    case AUTH_ACTIONS.PROVIDER.SWITCH_FAILURE:
    case AUTH_ACTIONS.SESSION.REFRESH_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: {
          ...action.payload,
          message: getSafeErrorMessage(action.payload)
        },
        lastAction: action.type
      };

    // Reset Actions
    case AUTH_ACTIONS.RESET:
      return initialState;

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  // Operation refs to prevent duplicate operations
  const authOperationsRef = useRef({
    login: false,
    signup: false,
    sendOtp: false,
    verifyOtp: false,
    resendOtp: false,
    forgotPassword: false,
    resetPassword: false,
    socialAuth: false,
    logout: false
  });

  const handleEmailRegistration = useCallback(async (email, password) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.signup) {
      logInfo('Duplicate signup attempt blocked', { email });
      return;
    }

    authOperationsRef.current.signup = true;
    
    logUserAction('email_registration_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.EMAIL.SIGNUP_START });

    try {
      const result = await KeycloakService.handleEmailSignup(sanitizeText(email), password);

      if (result.success) {
        // Check if verification is needed
        if (result.status === 'VERIFICATION_NEEDED') {
          dispatch({
            type: AUTH_ACTIONS.OTP.SEND_SUCCESS,
            payload: { email: sanitizeText(result.email || email) }
          });

          logInfo('Email registration requires verification', { email });
          logUserAction('email_registration_verification_needed', { email });

          return {
            status: 'VERIFICATION_NEEDED',
            success: true,
            email: sanitizeText(result.email || email),
            provider: PROVIDERS.EMAIL
          };
        } else {
          // Only set as authenticated if no verification needed
          const user = {
            email: sanitizeText(result.email || email),
            provider: PROVIDERS.EMAIL,
            barrackId: result.barrackId
          };

          dispatch({
            type: AUTH_ACTIONS.EMAIL.SIGNUP_SUCCESS,
            payload: {
              user,
              sessionInfo: {
                authenticated: true,
                lastRefresh: Date.now()
              }
            }
          });

          logInfo('Email registration successful', { email, barrackId: result.barrackId });
          logUserAction('email_registration_success', { email });

          return {
            status: 'REGISTERED',
            success: true,
            email: user.email,
            provider: PROVIDERS.EMAIL
          };
        }
      } else {
        throw {
          code: result.code || 'REGISTRATION_FAILED',
          message: result.message,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST (session expired during registration)
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'SESSION_EXPIRED',
          message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.SIGNUP_FAILURE,
          payload: safeError
        });
        logError('Session expired during registration', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.SIGNUP_FAILURE,
          payload: safeError
        });
        logError('Email registration failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.SIGNUP_FAILURE,
          payload: safeError
        });
        logError('Email registration failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.SIGNUP_FAILURE,
          payload: safeError
        });
        logError('Email registration network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'REGISTRATION_FAILED',
        message: getSafeErrorMessage(error),
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.EMAIL.SIGNUP_FAILURE,
        payload: safeError
      });
      
      logError('Email registration failed', { email }, error);
      logUserAction('email_registration_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.signup = false;
    }
  }, []);

  const handleEmailLogin = useCallback(async (email, password) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.login) {
      logInfo('Duplicate login attempt blocked', { email });
      return;
    }

    authOperationsRef.current.login = true;

    logUserAction('email_login_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.EMAIL.LOGIN_START });

    try {
      const result = await KeycloakService.directLogin(sanitizeText(email), password);

      if (result.success) {
        const user = {
          email: sanitizeText(result.email || email),
          provider: PROVIDERS.EMAIL,
          barrackId: result.barrackId
        };

        dispatch({
          type: AUTH_ACTIONS.EMAIL.LOGIN_SUCCESS,
          payload: {
            user,
            sessionInfo: {
              authenticated: true,
              lastRefresh: Date.now()
            }
          }
        });

        logInfo('Email login successful', { email, barrackId: result.barrackId });
        logUserAction('email_login_success', { email });

        return {
          status: 'SUCCESS',
          success: true,
          email: user.email,
          provider: PROVIDERS.EMAIL
        };
      } else {
        throw {
          code: result.code || 'LOGIN_FAILED',
          message: result.message,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'INVALID_CREDENTIALS',
          message: GENERIC_ERROR_MESSAGES['INVALID_CREDENTIALS']
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.LOGIN_FAILURE,
          payload: safeError
        });
        logError('Invalid credentials during login', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.LOGIN_FAILURE,
          payload: safeError
        });
        logError('Email login failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.LOGIN_FAILURE,
          payload: safeError
        });
        logError('Email login failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.EMAIL.LOGIN_FAILURE,
          payload: safeError
        });
        logError('Email login network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'LOGIN_FAILED',
        message: getSafeErrorMessage(error),
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.EMAIL.LOGIN_FAILURE,
        payload: safeError
      });
      
      logError('Email login failed', { email }, error);
      logUserAction('email_login_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.login = false;
    }
  }, []);

  const handleSendLoginOtp = useCallback(async (email) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.sendOtp) {
      logInfo('Duplicate send OTP attempt blocked', { email });
      return;
    }

    authOperationsRef.current.sendOtp = true;

    logUserAction('send_login_otp_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.OTP.SEND_START });

    try {
      const result = await KeycloakService.sendLoginOTP(sanitizeText(email));

      if (result.success) {
        dispatch({
          type: AUTH_ACTIONS.OTP.SEND_SUCCESS,
          payload: { email: sanitizeText(email) }
        });

        logInfo('Login OTP sent successfully', { email });
        logUserAction('send_login_otp_success', { email });

        return {
          success: true,
          email: sanitizeText(email),
          message: result.message || 'OTP sent successfully'
        };
      } else {
        throw {
          code: result.code || 'SEND_OTP_FAILED',
          message: result.message,
          retry_time: result.retry_time,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'SESSION_EXPIRED',
          message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.SEND_FAILURE,
          payload: safeError
        });
        logError('Session expired during OTP send', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.SEND_FAILURE,
          payload: safeError
        });
        logError('Send OTP failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.SEND_FAILURE,
          payload: safeError
        });
        logError('Send OTP failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.SEND_FAILURE,
          payload: safeError
        });
        logError('Send OTP network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'SEND_OTP_FAILED',
        message: getSafeErrorMessage(error),
        retry_time: error.retry_time,
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.OTP.SEND_FAILURE,
        payload: safeError
      });
      
      logError('Send login OTP failed', { email }, error);
      logUserAction('send_login_otp_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.sendOtp = false;
    }
  }, []);

  const handleVerifyLoginOtp = useCallback(async (email, otp) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.verifyOtp) {
      logInfo('Duplicate verify OTP attempt blocked', { email });
      return;
    }

    authOperationsRef.current.verifyOtp = true;

    logUserAction('verify_login_otp_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.OTP.VERIFY_START });

    try {
      const result = await KeycloakService.verifyLoginOTP(sanitizeText(email), sanitizeText(otp));

      if (result.success) {
        const user = {
          email: sanitizeText(result.email || email),
          provider: 'email_otp',
          barrackId: result.barrackId
        };

        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_SUCCESS,
          payload: {
            user,
            sessionInfo: {
              authenticated: true,
              lastRefresh: Date.now()
            }
          }
        });

        logInfo('Login OTP verified successfully', { email, barrackId: result.barrackId });
        logUserAction('verify_login_otp_success', { email });

        return {
          success: true,
          email: user.email,
          provider: 'email_otp',
          message: result.message || 'Login successful'
        };
      } else {
        throw {
          code: result.code || 'VERIFY_OTP_FAILED',
          message: result.message,
          retry_time: result.retry_time,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'VERIFY_OTP_FAILED',
          message: GENERIC_ERROR_MESSAGES['VERIFY_OTP_FAILED']
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Invalid OTP during verification', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Verify OTP failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Verify OTP failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Verify OTP network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'VERIFY_OTP_FAILED',
        message: getSafeErrorMessage(error),
        retry_time: error.retry_time,
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
        payload: safeError
      });
      
      logError('Verify login OTP failed', { email }, error);
      logUserAction('verify_login_otp_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.verifyOtp = false;
    }
  }, []);

  const handleResendLoginOtp = useCallback(async (email) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.resendOtp) {
      logInfo('Duplicate resend OTP attempt blocked', { email });
      return;
    }

    authOperationsRef.current.resendOtp = true;

    logUserAction('resend_login_otp_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.OTP.RESEND_START });

    try {
      const result = await KeycloakService.sendLoginOTP(sanitizeText(email));

      if (result.success) {
        dispatch({
          type: AUTH_ACTIONS.OTP.RESEND_SUCCESS,
          payload: { email: sanitizeText(email) }
        });

        logInfo('Login OTP resent successfully', { email });
        logUserAction('resend_login_otp_success', { email });

        return {
          success: true,
          email: sanitizeText(email),
          message: result.message || 'OTP resent successfully'
        };
      } else {
        throw {
          code: result.code || 'SEND_OTP_FAILED',
          message: result.message,
          retry_time: result.retry_time,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'SESSION_EXPIRED',
          message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
          payload: safeError
        });
        logError('Session expired during OTP resend', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
          payload: safeError
        });
        logError('Resend OTP failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
          payload: safeError
        });
        logError('Resend OTP failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
          payload: safeError
        });
        logError('Resend OTP network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'SEND_OTP_FAILED',
        message: getSafeErrorMessage(error),
        retry_time: error.retry_time,
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
        payload: safeError
      });
      
      logError('Resend login OTP failed', { email }, error);
      logUserAction('resend_login_otp_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.resendOtp = false;
    }
  }, []);

  const handleForgotPassword = useCallback(async (email) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.forgotPassword) {
      logInfo('Duplicate forgot password attempt blocked', { email });
      return;
    }

    authOperationsRef.current.forgotPassword = true;

    logUserAction('forgot_password_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.PASSWORD.FORGOT_START });

    try {
      const result = await KeycloakService.forgotPassword(sanitizeText(email));

      if (result.success) {
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.FORGOT_SUCCESS,
          payload: { 
            email: sanitizeText(email),
            status: result.status 
          }
        });

        logInfo('Forgot password request successful', { email, status: result.status });
        logUserAction('forgot_password_success', { email });

        return {
          success: true,
          email: sanitizeText(email),
          status: result.status || 'VERIFICATION_NEEDED',
          message: result.message || 'Password reset email sent'
        };
      } else {
        throw {
          code: result.code || 'FORGOT_PASSWORD_FAILED',
          message: result.message,
          retry_time: result.retry_time,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'SESSION_EXPIRED',
          message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.FORGOT_FAILURE,
          payload: safeError
        });
        logError('Session expired during forgot password', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.FORGOT_FAILURE,
          payload: safeError
        });
        logError('Forgot password failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.FORGOT_FAILURE,
          payload: safeError
        });
        logError('Forgot password failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.FORGOT_FAILURE,
          payload: safeError
        });
        logError('Forgot password network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'FORGOT_PASSWORD_FAILED',
        message: getSafeErrorMessage(error),
        retry_time: error.retry_time,
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.PASSWORD.FORGOT_FAILURE,
        payload: safeError
      });
      
      logError('Forgot password failed', { email }, error);
      logUserAction('forgot_password_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.forgotPassword = false;
    }
  }, []);

  const handleResetPassword = useCallback(async (email, otp, newPassword) => {
    // Prevent duplicate operations
    if (authOperationsRef.current.resetPassword) {
      logInfo('Duplicate reset password attempt blocked', { email });
      return;
    }

    authOperationsRef.current.resetPassword = true;

    logUserAction('reset_password_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.PASSWORD.RESET_START });

    try {
      const result = await KeycloakService.resetPassword(sanitizeText(email), sanitizeText(otp), newPassword);

      if (result.success) {
        const payload = {
          email: sanitizeText(email),
          autoLogin: result.autoLogin || false,
          message: result.message || 'Password reset successful'
        };

        if (result.autoLogin) {
          payload.user = {
            email: sanitizeText(email),
            provider: PROVIDERS.EMAIL
          };
          payload.sessionInfo = {
            authenticated: true,
            lastRefresh: Date.now()
          };
        }

        dispatch({
          type: AUTH_ACTIONS.PASSWORD.RESET_SUCCESS,
          payload
        });

        logInfo('Password reset successful', { email, autoLogin: result.autoLogin });
        logUserAction('reset_password_success', { email });

        return {
          success: true,
          email: sanitizeText(email),
          autoLogin: result.autoLogin || false,
          message: result.message || 'Password reset successful'
        };
      } else {
        throw {
          code: result.code || 'RESET_PASSWORD_FAILED',
          message: result.message,
          retry_time: result.retry_time,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'VERIFY_OTP_FAILED',
          message: GENERIC_ERROR_MESSAGES['VERIFY_OTP_FAILED']
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.RESET_FAILURE,
          payload: safeError
        });
        logError('Invalid OTP during password reset', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.RESET_FAILURE,
          payload: safeError
        });
        logError('Reset password failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.RESET_FAILURE,
          payload: safeError
        });
        logError('Reset password failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          retry_time: error.retry_time,
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.PASSWORD.RESET_FAILURE,
          payload: safeError
        });
        logError('Reset password network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'RESET_PASSWORD_FAILED',
        message: getSafeErrorMessage(error),
        retry_time: error.retry_time,
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.PASSWORD.RESET_FAILURE,
        payload: safeError
      });
      
      logError('Reset password failed', { email }, error);
      logUserAction('reset_password_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.resetPassword = false;
    }
  }, []);

const handleVerifyRegistrationOtp = useCallback(async (email, otp, password) => {    // Prevent duplicate operations
    if (authOperationsRef.current.verifyOtp) {
      logInfo('Duplicate verify registration OTP attempt blocked', { email });
      return;
    }

    authOperationsRef.current.verifyOtp = true;

    logUserAction('verify_registration_otp_initiated', { email });
    dispatch({ type: AUTH_ACTIONS.OTP.VERIFY_START });

    try {
const result = await KeycloakService.verifyRegistrationOTP(sanitizeText(email), sanitizeText(otp), password);
      if (result.success) {
        const user = {
          email: sanitizeText(result.email || email),
          provider: PROVIDERS.EMAIL,
          barrackId: result.barrackId
        };

        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_SUCCESS,
          payload: {
            user,
            sessionInfo: {
              authenticated: true,
              lastRefresh: Date.now()
            }
          }
        });

        logInfo('Registration OTP verified successfully', { email, barrackId: result.barrackId });
        logUserAction('verify_registration_otp_success', { email });

        return {
          success: true,
          email: user.email,
          provider: PROVIDERS.EMAIL
        };
      } else {
        throw {
          code: result.code || 'VERIFY_OTP_FAILED',
          message: result.message,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'VERIFY_OTP_FAILED',
          message: GENERIC_ERROR_MESSAGES['VERIFY_OTP_FAILED']
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Invalid OTP during registration verification', { email }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Verify registration OTP failed with backend code', { email, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Verify registration OTP failed with error code', { email, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
          payload: safeError
        });
        logError('Verify registration OTP network error', { email }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'VERIFY_OTP_FAILED',
        message: getSafeErrorMessage(error),
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.OTP.VERIFY_FAILURE,
        payload: safeError
      });
      
      logError('Verify registration OTP failed', { email }, error);
      logUserAction('verify_registration_otp_failed', { email });
      throw safeError;
    } finally {
      authOperationsRef.current.verifyOtp = false;
    }
  }, []);
const handleResendVerificationOtp = useCallback(async (email, password) => {
  // Prevent duplicate operations
  if (authOperationsRef.current.resendOtp) {
    logInfo('Duplicate resend verification OTP attempt blocked', { email });
    return;
  }

  authOperationsRef.current.resendOtp = true;

  logUserAction('resend_verification_otp_initiated', { email });
  dispatch({ type: AUTH_ACTIONS.OTP.RESEND_START });

  try {
    // ✅ CHANGED THIS LINE - now calls correct endpoint
    const result = await KeycloakService.resendResetPasswordOtp(sanitizeText(email));

    if (result.success) {
      dispatch({
        type: AUTH_ACTIONS.OTP.RESEND_SUCCESS,
        payload: { email: sanitizeText(email) }
      });

      logInfo('Verification OTP resent successfully', { email });
      logUserAction('resend_verification_otp_success', { email });

      return {
        success: true,
        email: sanitizeText(email),
        message: 'Verification email resent successfully'
      };
    } else {
      throw {
        code: result.code || 'SEND_OTP_FAILED',
        message: result.message,
        details: result.details
      };
    }
  } catch (error) {
    // ✅ 1. Check 401 FIRST
    if (error.response?.status === 401 || error.status === 401) {
      const safeError = {
        code: 'SESSION_EXPIRED',
        message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
      };
      dispatch({
        type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
        payload: safeError
      });
      logError('Session expired during verification OTP resend', { email }, error);
      throw safeError;
    }

    // ✅ 2. Check backend error codes
    if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
      const safeError = {
        code: error.response.data.code,
        message: GENERIC_ERROR_MESSAGES[error.response.data.code],
        details: error.details
      };
      dispatch({
        type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
        payload: safeError
      });
      logError('Resend verification OTP failed with backend code', { email, code: error.response.data.code }, error);
      throw safeError;
    }

    // ✅ 3. Check error.code
    if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
      const safeError = {
        code: error.code,
        message: GENERIC_ERROR_MESSAGES[error.code],
        details: error.details
      };
      dispatch({
        type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
        payload: safeError
      });
      logError('Resend verification OTP failed with error code', { email, code: error.code }, error);
      throw safeError;
    }

    // ✅ 4. Check network errors
    if (!error.response) {
      const safeError = {
        code: 'NETWORK_ERROR',
        message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
        details: error.details
      };
      dispatch({
        type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
        payload: safeError
      });
      logError('Resend verification OTP network error', { email }, error);
      throw safeError;
    }

    // ✅ 5. Fallback
    const safeError = {
      code: error.code || 'SEND_OTP_FAILED',
      message: getSafeErrorMessage(error),
      details: error.details
    };
    
    dispatch({
      type: AUTH_ACTIONS.OTP.RESEND_FAILURE,
      payload: safeError
    });
    
    logError('Resend verification OTP failed', { email }, error);
    logUserAction('resend_verification_otp_failed', { email });
    throw safeError;
  } finally {
    authOperationsRef.current.resendOtp = false;
  }
}, []);

  const handleSocialAuth = useCallback(async (provider, mode = 'login', code = null, state = null) => {
    // If this is a callback with code and state, handle OAuth callback
    if (code && state) {
      return handleOAuthCallback(code, state, provider);
    }

    // Prevent duplicate operations
    if (authOperationsRef.current.socialAuth) {
      logInfo('Duplicate social auth attempt blocked', { provider });
      return;
    }

    authOperationsRef.current.socialAuth = true;

    logUserAction('social_auth_initiated', { provider, mode });
    dispatch({
      type: AUTH_ACTIONS.OAUTH.START,
      payload: { provider }
    });

    try {
      const result = await KeycloakService.initiateSocialLogin(provider, mode);

      if (result.success && result.authUrl) {
        logInfo('Social auth initialized successfully', { provider, mode });
        logUserAction('social_auth_initialized', { provider });

        return {
          status: 'INITIALIZED',
          authUrl: result.authUrl,
          state: result.state
        };
      } else {
        throw {
          code: result.code || 'OAUTH_INITIALIZATION_ERROR',
          message: result.message
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'SESSION_EXPIRED',
          message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.FAILURE,
          payload: safeError
        });
        logError('Session expired during social auth initialization', { provider }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code]
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.FAILURE,
          payload: safeError
        });
        logError('Social auth failed with backend code', { provider, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code]
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.FAILURE,
          payload: safeError
        });
        logError('Social auth failed with error code', { provider, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR']
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.FAILURE,
          payload: safeError
        });
        logError('Social auth network error', { provider }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'OAUTH_INITIALIZATION_ERROR',
        message: getSafeErrorMessage(error)
      };
      
      dispatch({
        type: AUTH_ACTIONS.OAUTH.FAILURE,
        payload: safeError
      });
      
      logError('Social auth initialization failed', { provider }, error);
      logUserAction('social_auth_failed', { provider });
      throw safeError;
    } finally {
      authOperationsRef.current.socialAuth = false;
    }
  }, []);

  const handleOAuthCallback = useCallback(async (code, state, provider) => {
    logUserAction('oauth_callback_initiated', { provider });
    dispatch({
      type: AUTH_ACTIONS.OAUTH.CALLBACK_START,
      payload: { provider }
    });

    try {
      const result = await KeycloakService.handleSocialCallback(code, state, provider);

      if (result.success) {
        const user = {
          email: sanitizeText(result.email),
          provider: result.provider || provider,
          barrackId: result.barrackId,
          isNewUser: result.isNewUser
        };

        dispatch({
          type: AUTH_ACTIONS.OAUTH.CALLBACK_SUCCESS,
          payload: {
            user,
            provider: user.provider,
            sessionInfo: result.sessionInfo || {
              authenticated: true,
              lastRefresh: Date.now()
            }
          }
        });

        logInfo('OAuth callback successful', { provider, email: user.email, barrackId: result.barrackId });
        logUserAction('oauth_callback_success', { provider });

        // Handle redirect after successful OAuth
        const redirectPath = localStorage.getItem('auth_redirect_path');
        if (redirectPath && isValidRedirectPath(redirectPath)) {
          localStorage.removeItem('auth_redirect_path');
          navigate(redirectPath, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }

        return {
          status: 'SUCCESS',
          user,
          provider: user.provider,
          sessionInfo: result.sessionInfo
        };
      } else {
        throw {
          code: result.code || 'OAUTH_CALLBACK_FAILED',
          message: result.message,
          details: result.details
        };
      }
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'AUTHENTICATION_FAILED',
          message: GENERIC_ERROR_MESSAGES['AUTHENTICATION_FAILED']
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.CALLBACK_FAILURE,
          payload: safeError
        });
        logError('OAuth callback authentication failed', { provider }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.CALLBACK_FAILURE,
          payload: safeError
        });
        logError('OAuth callback failed with backend code', { provider, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.CALLBACK_FAILURE,
          payload: safeError
        });
        logError('OAuth callback failed with error code', { provider, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          details: error.details
        };
        dispatch({
          type: AUTH_ACTIONS.OAUTH.CALLBACK_FAILURE,
          payload: safeError
        });
        logError('OAuth callback network error', { provider }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'OAUTH_CALLBACK_FAILED',
        message: getSafeErrorMessage(error),
        details: error.details
      };
      
      dispatch({
        type: AUTH_ACTIONS.OAUTH.CALLBACK_FAILURE,
        payload: safeError
      });
      
      logError('OAuth callback failed', { provider }, error);
      logUserAction('oauth_callback_failed', { provider });
      throw safeError;
    }
  }, [navigate]);

  const isValidOAuthUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      
      if (parsedUrl.protocol !== 'https:') {
        return false;
      }
      
      const allowedDomains = ['accounts.google.com', 'barrack.ai'];
      return allowedDomains.includes(parsedUrl.hostname);
    } catch {
      return false;
    }
  };

  const handleProviderSwitch = useCallback(async (newProvider, email) => {
    logUserAction('provider_switch_initiated', { newProvider, email });
    dispatch({
      type: AUTH_ACTIONS.PROVIDER.SWITCH_START,
      payload: { provider: newProvider }
    });

    try {
      if (newProvider === PROVIDERS.EMAIL) {
        navigate('/login', { state: { email: sanitizeText(email) } });
        logInfo('Provider switch to email', { email });
        return;
      }

      const result = await handleSocialAuth(newProvider, 'login');
      if (result.status === 'INITIALIZED' && result.authUrl) {
        if (isValidOAuthUrl(result.authUrl)) {
          window.location.href = result.authUrl;
          logInfo('Provider switch to social', { newProvider });
        } else {
          throw new Error('Authentication service unavailable');
        }
      }

      return result;
    } catch (error) {
      // ✅ 1. Check 401 FIRST
      if (error.response?.status === 401 || error.status === 401) {
        const safeError = {
          code: 'SESSION_EXPIRED',
          message: GENERIC_ERROR_MESSAGES['SESSION_EXPIRED']
        };
        dispatch({
          type: AUTH_ACTIONS.PROVIDER.SWITCH_FAILURE,
          payload: safeError
        });
        logError('Session expired during provider switch', { newProvider }, error);
        throw safeError;
      }

      // ✅ 2. Check backend error codes
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        const safeError = {
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code]
        };
        dispatch({
          type: AUTH_ACTIONS.PROVIDER.SWITCH_FAILURE,
          payload: safeError
        });
        logError('Provider switch failed with backend code', { newProvider, code: error.response.data.code }, error);
        throw safeError;
      }

      // ✅ 3. Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        const safeError = {
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code]
        };
        dispatch({
          type: AUTH_ACTIONS.PROVIDER.SWITCH_FAILURE,
          payload: safeError
        });
        logError('Provider switch failed with error code', { newProvider, code: error.code }, error);
        throw safeError;
      }

      // ✅ 4. Check network errors
      if (!error.response) {
        const safeError = {
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR']
        };
        dispatch({
          type: AUTH_ACTIONS.PROVIDER.SWITCH_FAILURE,
          payload: safeError
        });
        logError('Provider switch network error', { newProvider }, error);
        throw safeError;
      }

      // ✅ 5. Fallback
      const safeError = {
        code: error.code || 'PROVIDER_SWITCH_FAILED',
        message: getSafeErrorMessage(error)
      };
      
      dispatch({
        type: AUTH_ACTIONS.PROVIDER.SWITCH_FAILURE,
        payload: safeError
      });
      
      logError('Provider switch failed', { newProvider }, error);
      logUserAction('provider_switch_failed', { newProvider });
      throw safeError;
    }
  }, [navigate, handleSocialAuth]);

  const logout = useCallback(async () => {
    // Prevent duplicate operations
    if (authOperationsRef.current.logout) {
      logInfo('Duplicate logout attempt blocked');
      return;
    }

    authOperationsRef.current.logout = true;

    logUserAction('logout_initiated');

    try {
      await KeycloakService.logout();
      tokenManager.stopProactiveRefresh();
      dispatch({ type: AUTH_ACTIONS.RESET });
      navigate('/login', { replace: true });
      
      logInfo('Logout successful');
      logUserAction('logout_success');
    } catch (error) {
      // Even if logout fails, clear local state
      tokenManager.stopProactiveRefresh();
      dispatch({ type: AUTH_ACTIONS.RESET });
      navigate('/login', { replace: true });
      
      logError('Logout error (state cleared anyway)', {}, error);
    } finally {
      authOperationsRef.current.logout = false;
    }
  }, [navigate]);

  const handleAuthError = useCallback(() => {
    logInfo('Auth error triggered - session expired');
    dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
  }, []);

  // Initialize authentication state with optimistic auth
  useEffect(() => {
    const initAuth = async () => {
      if (state.isInitialized) return;
      
      logInfo('Initializing authentication');

      // Check for auth hint first (optimistic authentication)
      const hasAuthHint = getAuthHint();
      
      if (hasAuthHint) {
        logInfo('Auth hint found, setting optimistic authenticated state');
        
        // Set optimistic state immediately - user sees page right away
        dispatch({
          type: AUTH_ACTIONS.INITIALIZATION.SUCCESS,
          payload: {
            user: { email: '', provider: '' }, // Placeholder until verified
            sessionInfo: {
              authenticated: true,
              lastRefresh: Date.now(),
              isOptimistic: true // Flag to indicate this is optimistic
            }
          }
        });

        // Verify in background
        try {
          const result = await KeycloakService.checkSession();
          
          if (result.authenticated) {
            // Update with real user data
            const userInfo = {
              email: sanitizeText(result.email),
              provider: result.provider,
              preferred_currency: result.preferred_currency
            };
            
            dispatch({
              type: AUTH_ACTIONS.INITIALIZATION.SUCCESS,
              payload: {
                user: userInfo,
                sessionInfo: {
                  authenticated: true,
                  lastRefresh: Date.now()
                }
              }
            });

            logInfo('Background session verification successful', { email: userInfo.email, provider: userInfo.provider });

            // Handle redirects after successful authentication
            const redirectPath = localStorage.getItem('auth_redirect_path');
            if (redirectPath && isValidRedirectPath(redirectPath)) {
              localStorage.removeItem('auth_redirect_path');
              navigate(redirectPath, { replace: true });
            }
          } else {
            // Hint was stale, session is invalid
            logInfo('Background verification failed - session invalid, hint was stale');
            dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
            navigate('/login', { replace: true });
          }
        } catch (error) {
          // Background verification failed
          logError('Background session verification error', {}, error);
          dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
          navigate('/login', { replace: true });
        }
      } else {
        // No auth hint - do normal flow (for new users or logged out users)
        logInfo('No auth hint found, checking session normally');
        dispatch({ type: AUTH_ACTIONS.INITIALIZATION.START });

        try {
          const result = await KeycloakService.checkSession();
          
          if (result.authenticated) {
            const userInfo = {
              email: sanitizeText(result.email),
              provider: result.provider,
              preferred_currency: result.preferred_currency
            };
            
            dispatch({
              type: AUTH_ACTIONS.INITIALIZATION.SUCCESS,
              payload: {
                user: userInfo,
                sessionInfo: {
                  authenticated: true,
                  lastRefresh: Date.now()
                }
              }
            });

            logInfo('Authentication initialization successful', { email: userInfo.email, provider: userInfo.provider });

            // Handle redirects after successful authentication
            const redirectPath = localStorage.getItem('auth_redirect_path');
            if (redirectPath && isValidRedirectPath(redirectPath)) {
              localStorage.removeItem('auth_redirect_path');
              navigate(redirectPath, { replace: true });
            }
          } else {
            dispatch({ type: AUTH_ACTIONS.INITIALIZATION.NO_SESSION });
            
            logInfo('No active session found');

            // Check for auth error parameters
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has('error')) {
              dispatch({
                type: AUTH_ACTIONS.ERROR.SET,
                payload: {
                  code: 'AUTHENTICATION_FAILED',
                  message: getSafeErrorMessage({ code: 'AUTHENTICATION_FAILED' })
                }
              });
            }
          }
        } catch (error) {
          // ✅ 1. Check 401 FIRST
          if (error.response?.status === 401 || error.status === 401) {
            dispatch({ type: AUTH_ACTIONS.INITIALIZATION.NO_SESSION });
            logInfo('401 during initialization - no active session');
            return;
          }

          // ✅ 2. Check backend error codes
          if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
            const safeError = {
              code: error.response.data.code,
              message: GENERIC_ERROR_MESSAGES[error.response.data.code]
            };
            dispatch({
              type: AUTH_ACTIONS.INITIALIZATION.FAILURE,
              payload: safeError
            });
            logError('Auth initialization failed with backend code', { code: error.response.data.code }, error);
            return;
          }

          // ✅ 3. Check error.code
          if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
            const safeError = {
              code: error.code,
              message: GENERIC_ERROR_MESSAGES[error.code]
            };
            dispatch({
              type: AUTH_ACTIONS.INITIALIZATION.FAILURE,
              payload: safeError
            });
            logError('Auth initialization failed with error code', { code: error.code }, error);
            return;
          }

          // ✅ 4. Check network errors
          if (!error.response) {
            const safeError = {
              code: 'NETWORK_ERROR',
              message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR']
            };
            dispatch({
              type: AUTH_ACTIONS.INITIALIZATION.FAILURE,
              payload: safeError
            });
            logError('Auth initialization network error', {}, error);
            return;
          }

          // ✅ 5. Fallback
          const safeError = {
            code: error.code || 'SERVER_ERROR',
            message: getSafeErrorMessage(error)
          };
          
          dispatch({
            type: AUTH_ACTIONS.INITIALIZATION.FAILURE,
            payload: safeError
          });
          
          logError('Auth initialization failed', {}, error);
        }
      }
    };

    initAuth();
  }, [navigate, state.isInitialized]);

  // Tab sync listener - sync auth state across browser tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Only handle auth_hint changes
      if (event.key !== 'auth_hint') return;
      
      logInfo('Storage event detected', { 
        key: event.key, 
        oldValue: event.oldValue, 
        newValue: event.newValue 
      });

      // Auth hint was removed (logout in another tab)
      if (event.oldValue === 'true' && event.newValue === null) {
        logInfo('Auth hint removed in another tab - logging out');
        tokenManager.stopProactiveRefresh();
        dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
        navigate('/login', { replace: true });
      }
      
      // Auth hint was added (login in another tab while this tab shows login page)
      if (event.oldValue === null && event.newValue === 'true') {
        logInfo('Auth hint added in another tab - refreshing auth state');
        // Optionally refresh the page or re-check session
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  // Listen for external auth state changes (from KeycloakService)
  useEffect(() => {
    const handleAuthStateChange = (event) => {
      const { authenticated, user, sessionInfo } = event.detail;
      
      if (authenticated && user) {
        const sanitizedUser = {
          email: sanitizeText(user.email),
          provider: user.provider,
          barrackId: user.barrackId,
          preferred_currency: user.preferred_currency
        };
        
        dispatch({
          type: AUTH_ACTIONS.INITIALIZATION.SUCCESS,
          payload: { 
            user: sanitizedUser, 
            sessionInfo: sessionInfo || { authenticated: true, lastRefresh: Date.now() }
          }
        });

        logInfo('Auth state changed', { authenticated, email: sanitizedUser.email });
      } else {
        dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
        logInfo('Auth state changed - session expired');
      }
    };

    const handleLogout = () => {
      dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
      logInfo('Logout event received');
    };

    window.addEventListener('auth:stateChanged', handleAuthStateChange);
    window.addEventListener('auth:logout', handleLogout);
    
    return () => {
      window.removeEventListener('auth:stateChanged', handleAuthStateChange);
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // Handle session expiration
  useEffect(() => {
    const handleSessionExpired = () => {
      dispatch({ type: AUTH_ACTIONS.SESSION.EXPIRED });
      
      logInfo('Session expired event received');
      
      navigate('/login', {
        state: {
          message: 'Your session has expired. Please sign in again.',
          returnUrl: location.pathname
        }
      });
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('auth:sessionExpired', handleSessionExpired);
  }, [navigate, location.pathname]);

  // Clear error helper
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.ERROR.CLEAR });
  }, []);

  const value = useMemo(() => ({
    ...state,
    handleEmailRegistration,
    handleEmailLogin,
    handleSendLoginOtp,
    handleVerifyLoginOtp,
    handleResendLoginOtp,
    handleForgotPassword,
    handleResetPassword,
    handleVerifyRegistrationOtp,
    handleResendVerificationOtp,
    handleSocialAuth,
    handleOAuthCallback,
    handleProviderSwitch,
    logout,
    clearError,
    handleAuthError
  }), [
    state,
    handleEmailRegistration,
    handleEmailLogin,
    handleSendLoginOtp,
    handleVerifyLoginOtp,
    handleResendLoginOtp,
    handleForgotPassword,
    handleResetPassword,
    handleVerifyRegistrationOtp,
    handleResendVerificationOtp,
    handleSocialAuth,
    handleOAuthCallback,
    handleProviderSwitch,
    logout,
    clearError,
    handleAuthError
  ]);

  return (
    <AuthStateContext.Provider value={value}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthStateContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const useAuthDispatch = () => {
  const context = useContext(AuthDispatchContext);
  if (!context) {
    throw new Error('useAuthDispatch must be used within AuthProvider');
  }
  return context;
};

export default AuthProvider;